import { ref, watch } from "vue";
import { defineStore, storeToRefs } from "pinia";
import { isValidEmailAddress } from "../utils/validators";

import { useBuilder } from "./builder.store";
import { useNotifications } from "./notifications.store";
import { useClipboard } from '@vueuse/core';

import shareResumeApi from "../api/share-resume";

const useResumeShare = defineStore("resumeShare", () => {
    const showShareModal = ref(false);
    const builderStore = useBuilder();
    
    const { data: resumeData } = storeToRefs(builderStore)
    
    const notificationsStore = useNotifications();
    const { copy } = useClipboard();

    const showRemoveUserModal = ref(false);
    const userToBeRemoved = ref(null);
    const loadingCopyShareLink = ref(false);
    const loadingShareResume = ref(false);
    const generalAccessValue= ref(null);
    const invitedUsersAccessRights = ref('viewer');
    const anyoneWithLinkAccessLevel = ref('viewer');

    const email = ref(null);
    const selectedUsers = ref([]);
    const emailInputRef = ref(null);
    const sharedWith = ref([]);

    const setShowRemoveUserModal = (user) => {
        userToBeRemoved.value = user;
        showRemoveUserModal.value = true;
    }

    const hideRemoveUserModal = () => {
        userToBeRemoved.value = null;
        showRemoveUserModal.value = false;
    }

    const validationErrors = ref({
        email: null
    });

    watch(resumeData, (newValue) => {
        generalAccessValue.value = newValue?.shared_links[0]?.allow_anonymous === 1 ? 'anyoneWithLink' : 'restricted';
        sharedWith.value = resumeData.value?.shared_users || [];
    })

    const setShowShareModal = () => {
        showShareModal.value = true
    }

    const hideShareModal = () => {
        showShareModal.value = false
    }

    const shareResumeWithUsers = async (resumeId, data) => {
        try {
            const response = await shareResumeApi.shareResumeWithUsers(resumeId, data);

            notificationsStore.notify('Resume was successfully shared!', 'success');
            
            return response;
        } catch (e) {
            notificationsStore.notify('Failed to share resume!', 'error');
        }
    }

    const copyLinkHandler = async () => {
        const share_link = resumeData.value?.shared_links?.[0];
        let shareToken;

        if(share_link?.link_token == null){
            loadingCopyShareLink.value = true;

            try {
                const response = await shareResume(false, 'viewer');
                shareToken = response.link_token
            }catch (e) {
                notificationsStore.notify('Failed to copy share link!', 'error');
                return;
            }finally {
                loadingCopyShareLink.value = false;
            }
        }else{
            shareToken = share_link?.link_token
        }

        copy(`${window.location.origin}/resume?token=${shareToken}`);
        notificationsStore.notify('Share link was copied!');
    }

    const shareResume = async (allowAnonymousUsers, anyoneWithLinkAccessLevel) => {
        loadingShareResume.value = true;

        try {
            const response = await shareResumeApi.shareResume(resumeData.value.uuid, {
                // permission: anyoneWithLinkAccessLevel,
                permission: 'viewer',
                allow_anonymous: allowAnonymousUsers
            });

            notificationsStore.notify('Resume was successfully shared!', 'success');

            return response;
        } catch (e) {
            notificationsStore('Failed to share resume!', 'error');
            return null;
        }finally {
            loadingShareResume.value = false;
        }
    }

    const shareResumeWithEmails = async () => {
        if(selectedUsers.value.length > 0) {
            loadingShareResume.value = true;

            try {
                const response = await shareResumeWithUsers(resumeData.value?.uuid, {
                    emails: selectedUsers.value,
                    // permission: invitedUsersAccessRights.value,
                    permission: 'viewer',
                    allow_anonymous: false
                });

                sharedWith.value = [...sharedWith.value, ...response];
            }catch (e) {
                notificationsStore('Failed to share resume!', 'error')
            } finally {
                loadingShareResume.value = false;
            }
        }

        closeShareModal();
    }
    
    const addEmailHandler = () => {
        if(email.value === '' || email.value == null) return;

        if(!isValidEmailAddress(email.value)) {
            validationErrors.value.email = 'Please enter a valid email';
            return;
        }

        selectedUsers.value.push(email.value);
        email.value = null;
    }

    const closeShareModal = () => {
        email.value = null;
        selectedUsers.value = [];
        emailInputRef.value = null;

        invitedUsersAccessRights.value = 'viewer';
        anyoneWithLinkAccessLevel.value = 'viewer';

        hideShareModal();
    }

    const emailInputChangeHandler = () => {
        if(email.value === '' || email.value == null) {
            validationErrors.value.email = null;
        }
    }

    const generalAccessChangeHandler = async (newValue) => {
        let allowAnonymousUsers = false;

        if(newValue === 'anyoneWithLink') {
            allowAnonymousUsers = true;
        }

        try {
            await shareResume(allowAnonymousUsers);
        }catch (e) {
            notificationsStore.notify('Failed to share resume!', 'error');
        }
    }

    const removeUserFromShareList = async () => {
        try {
            const userId = userToBeRemoved.value.userId;
            await shareResumeApi.revokeUserAccess(userId);
            hideRemoveUserModal();

            const removedUserId = sharedWith.value.findIndex(user => user.id === userId)
            const newValue = sharedWith.value;
            newValue.splice(removedUserId, 1);

            sharedWith.value = newValue;
        }catch (e) {
            notificationsStore.notify('Failed to revoke access!', 'error')
        }
    }

    const removeEmailHandler = (index) => {
        selectedUsers.value.splice(index, 1);
    }

    return {
        showShareModal,
        loadingCopyShareLink,
        generalAccessValue,
        invitedUsersAccessRights,
        anyoneWithLinkAccessLevel,
        sharedWith,
        loadingShareResume,
        email,
        selectedUsers,
        emailInputRef,
        validationErrors,
        showRemoveUserModal,
        userToBeRemoved,
        
        setShowShareModal,
        setShowRemoveUserModal,
        hideRemoveUserModal,
        hideShareModal,
        shareResumeWithUsers,
        copyLinkHandler,
        shareResume,
        shareResumeWithEmails,
        addEmailHandler,
        closeShareModal,
        emailInputChangeHandler,
        generalAccessChangeHandler,
        removeUserFromShareList,
        removeEmailHandler
    };
});

export { 
    useResumeShare,
}
